import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import 'react-leaflet-cluster/dist/assets/MarkerCluster.css';
import 'react-leaflet-cluster/dist/assets/MarkerCluster.Default.css';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Plus, List } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import CreatePinDialog from '@/components/CreatePinDialog';
import PinDetailPanel from '@/components/PinDetailPanel';
import FilterBar from '@/components/FilterBar';
import ListPanel from '@/components/ListPanel';
import LoginGate from '@/components/LoginGate';
import MyPinsDialog from '@/components/MyPinsDialog';
import AccountSettingsDialog from '@/components/AccountSettingsDialog';
import WelcomeDialog from '@/components/WelcomeDialog';
import ForceRenameDialog from '@/components/ForceRenameDialog';
import TopBar from '@/components/TopBar';
import PinMarkers from '@/components/map/PinMarkers';
import { RecenterButton, MapClickHandler } from '@/components/map/MapControls';
import { userIcon, newPinIcon, IS_TOUCH } from '@/lib/mapIcons';

export default function MapPage() {
  const { t } = useTranslation();
  const { user, login, logout, loading, setUser } = useAuth();
  const [center, setCenter] = useState(null);
  const [pins, setPins] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selectedPin, setSelectedPin] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [myPinsOpen, setMyPinsOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [pendingLatLng, setPendingLatLng] = useState(null);
  const [editingPin, setEditingPin] = useState(null);
  const [site, setSite] = useState({ logo_text_left: 'Curb', logo_text_right: 'Alert' });
  const mapRef = useRef(null);
  const [currentZoom, setCurrentZoom] = useState(14);
  const [mapBounds, setMapBounds] = useState(null);
  const [listOpen, setListOpen] = useState(false);
  const [previewPinId, setPreviewPinId] = useState(null);
  const [locating, setLocating] = useState(false);
  const lastTapRef = useRef({ id: null, time: 0 });

  // Freeze map pan/zoom while the create dialog is open so the background
  // doesn't drift under the panel on touch devices (the green pin stays draggable).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const handlers = ['dragging', 'touchZoom', 'scrollWheelZoom', 'boxZoom', 'keyboard'];
    handlers.forEach(h => map[h] && (createOpen ? map[h].disable() : map[h].enable()));
  }, [createOpen]);


  const locateMe = useCallback((announce = false) => {
    if (!navigator.geolocation) {
      setCenter(prev => prev || [40.7128, -74.006]);
      if (announce) toast.error(t('map.geoUnsupported'));
      return;
    }
    setLocating(true);
    const success = (pos) => {
      const c = [pos.coords.latitude, pos.coords.longitude];
      setCenter(c);
      if (mapRef.current) mapRef.current.flyTo(c, 14);
      setLocating(false);
    };
    const fail = (err, isRetry) => {
      // High-accuracy GPS often times out (esp. Android/indoors) — retry with
      // faster network-based location before giving up.
      if (err && err.code === err.TIMEOUT && !isRetry) {
        navigator.geolocation.getCurrentPosition(
          success, (e) => fail(e, true),
          { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
        );
        return;
      }
      setLocating(false);
      setCenter(prev => prev || [40.7128, -74.006]);
      if (announce) {
        toast.error(
          err && err.code === err.PERMISSION_DENIED
            ? t('map.geoDenied')
            : t('map.geoUnavailable'),
        );
      }
    };
    navigator.geolocation.getCurrentPosition(
      success, (e) => fail(e, false),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 },
    );
  }, [t]);

  const handleMarkerTap = useCallback((p) => {
    if (!IS_TOUCH) { setSelectedPin(p); return; }
    const now = Date.now();
    const last = lastTapRef.current;
    if (last.id === p.id && now - last.time < 450) {
      setPreviewPinId(null);
      setSelectedPin(p);
      lastTapRef.current = { id: null, time: 0 };
    } else {
      setSelectedPin(null);
      setPreviewPinId(p.id);
      lastTapRef.current = { id: p.id, time: now };
    }
  }, []);

  const loadPins = useCallback(async () => {
    try {
      const res = await api.get('/pins');
      setPins(res.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => { loadPins(); }, [loadPins]);
  useEffect(() => {
    // Mount-only: fetch site content once on load (no reactive deps).
    api.get('/site-content').then(r => setSite(r.data)).catch((e) => console.debug('site-content fetch failed:', e?.message || e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link: open a shared pin via ?pin=<id> (runs once on mount).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pinId = params.get('pin');
    if (!pinId) return;
    api.get(`/pins/${pinId}`).then(r => {
      setSelectedPin(r.data);
      if (mapRef.current) mapRef.current.flyTo([r.data.lat, r.data.lng], 16);
    }).catch(() => {}).finally(() => {
      window.history.replaceState({}, '', window.location.pathname);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    locateMe(false);
    // Don't block the map forever if location is slow/unavailable.
    const t = setTimeout(() => setCenter(prev => prev || [40.7128, -74.006]), 3500);
    return () => clearTimeout(t);
  }, [locateMe]);

  // Persist last-known location for nearby alerts (logged-in users only)
  useEffect(() => {
    if (user && center) {
      api.post('/me/location', { lat: center[0], lng: center[1] }).catch(() => {});
    }
  }, [user, center]);

  const filteredPins = useMemo(() => {
    if (filter === 'all') return pins;
    return pins.filter(p => p.category === filter);
  }, [pins, filter]);

  const visiblePins = useMemo(() => {
    if (!mapBounds) return pins;
    return pins.filter(p => mapBounds.contains([p.lat, p.lng]));
  }, [pins, mapBounds]);

  const listPins = useMemo(() => {
    return filter === 'all' ? visiblePins : visiblePins.filter(p => p.category === filter);
  }, [visiblePins, filter]);

  const handleMapDblClick = (latlng) => {
    if (!user) { toast.info(t('map.signInToDrop')); return; }
    setEditingPin(null);
    setPendingLatLng(latlng);
    setCreateOpen(true);
  };
  const handleMapSingleClick = () => {
    if (selectedPin) setSelectedPin(null);
    if (previewPinId) setPreviewPinId(null);
  };

  const onPinCreated = async () => {
    setCreateOpen(false);
    setPendingLatLng(null);
    await loadPins();
    toast.success(t('map.pinDropped'));
  };

  if (!center || loading) {
    return (
      <div className="app-screen flex items-center justify-center bg-brand-paper">
        <div className="font-display text-3xl text-brand-ink animate-pulse">{t('map.loading')}</div>
      </div>
    );
  }

  return (
    <div className="relative app-screen overflow-hidden" data-testid="map-page">
      <MapContainer
        center={center}
        zoom={14}
        zoomControl={false}
        doubleClickZoom={false}
        className={`absolute inset-0 z-0 leaflet-zoom-${currentZoom}`}
        ref={mapRef}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          attribution='&copy; OpenStreetMap &copy; CARTO'
        />
        <Marker position={center} icon={userIcon} />
        <PinMarkers pins={filteredPins} previewPinId={previewPinId} onMarkerTap={handleMarkerTap} />
        {createOpen && pendingLatLng && (
          <Marker
            position={[pendingLatLng.lat, pendingLatLng.lng]}
            icon={newPinIcon}
            draggable={true}
            eventHandlers={{
              dragend: (e) => {
                const pos = e.target.getLatLng();
                setPendingLatLng({ lat: pos.lat, lng: pos.lng });
              },
            }}
          />
        )}
        <MapClickHandler onDblClick={handleMapDblClick} onSingleClick={handleMapSingleClick} onZoom={setCurrentZoom} onBounds={setMapBounds} />
      </MapContainer>

      {/* Locate-me button — shown for everyone so users can grant/retry GPS */}
      <RecenterButton onClick={() => locateMe(true)} locating={locating} />

      {/* Top bar */}
      <TopBar
        user={user}
        login={login}
        logout={logout}
        site={site}
        onMyPins={() => setMyPinsOpen(true)}
        onAccount={() => setAccountOpen(true)}
        onSelectPin={(pinId) => {
          api.get(`/pins/${pinId}`).then(r => {
            setSelectedPin(r.data);
            if (mapRef.current) mapRef.current.flyTo([r.data.lat, r.data.lng], 16);
          }).catch(() => {});
        }}
      />

      {/* Filter bar + list view */}
      <div className="absolute top-24 sm:top-28 left-1/2 -translate-x-1/2 z-[600] flex flex-col items-center gap-3 w-full px-4 sm:px-0 sm:w-auto">
        <FilterBar
          filter={filter}
          setFilter={setFilter}
          counts={visiblePins}
          listOpen={listOpen}
          onToggleList={() => setListOpen(o => !o)}
        />
        <AnimatePresence>
          {listOpen && (
            <ListPanel
              pins={listPins}
              center={mapBounds ? mapBounds.getCenter() : null}
              onClose={() => setListOpen(false)}
              onSelect={(p) => { setSelectedPin(p); if (mapRef.current) mapRef.current.flyTo([p.lat, p.lng], Math.max(mapRef.current.getZoom(), 15)); }}
              onZoomIn={() => mapRef.current && mapRef.current.zoomIn()}
              onZoomOut={() => mapRef.current && mapRef.current.zoomOut()}
            />
          )}
        </AnimatePresence>
      </div>

      {/* Drop pin + My Pins floating buttons — hidden while the create dialog is open */}
      {user && !createOpen && (
        <div className="absolute bottom-6 left-6 z-[1000] flex items-center gap-3">
          <motion.button
            data-testid="create-pin-btn"
            initial={{ scale: 0 }} animate={{ scale: 1 }}
            whileHover={{ y: -3 }}
            onClick={() => { setPendingLatLng({ lat: center[0], lng: center[1] }); setCreateOpen(true); }}
            className="bg-brand hover:bg-brand-hover text-white rounded-full pl-5 pr-6 py-4 shadow-2xl flex items-center gap-2 font-display font-semibold"
          >
            <Plus className="w-5 h-5" /> {t('map.dropPin')}
          </motion.button>
          <motion.button
            data-testid="my-pins-btn"
            initial={{ scale: 0 }} animate={{ scale: 1 }}
            whileHover={{ y: -3 }}
            onClick={() => setMyPinsOpen(true)}
            className="glass-panel hover:bg-white text-brand-ink rounded-full pl-5 pr-6 py-4 shadow-xl flex items-center gap-2 font-display font-semibold"
          >
            <List className="w-5 h-5" /> {t('map.myPins')}
          </motion.button>
        </div>
      )}

      {/* Sign-in gate (hero) when not signed in - bottom-left card */}
      {!user && (
        <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-auto sm:max-w-md z-[500]">
          <LoginGate site={site} onLogin={login} />
        </div>
      )}

      {/* Pin detail */}
      <AnimatePresence>
        {selectedPin && (
          <PinDetailPanel
            pin={selectedPin}
            onClose={() => setSelectedPin(null)}
            onDeleted={async () => { setSelectedPin(null); await loadPins(); }}
            canEdit={!!user && (user.user_id === selectedPin.user_id || user.is_admin)}
            canReport={!!user && user.user_id !== selectedPin.user_id}
          />
        )}
      </AnimatePresence>

      {/* Create dialog */}
      <CreatePinDialog
        open={createOpen}
        onOpenChange={(o) => { setCreateOpen(o); if (!o) { setPendingLatLng(null); setEditingPin(null); } }}
        latlng={pendingLatLng}
        editingPin={editingPin}
        onLatLngChange={(ll) => {
          setPendingLatLng(ll);
          if (mapRef.current) mapRef.current.flyTo([ll.lat, ll.lng], 16);
        }}
        onCreated={onPinCreated}
      />

      {/* My Pins dialog */}
      <MyPinsDialog
        open={myPinsOpen}
        onOpenChange={setMyPinsOpen}
        onSelect={(p) => {
          setSelectedPin(p);
          if (mapRef.current) mapRef.current.flyTo([p.lat, p.lng], 16);
        }}
        onChanged={loadPins}
      />

      {/* Account Settings */}
      <AccountSettingsDialog open={accountOpen} onOpenChange={setAccountOpen} />
      {user && !user.onboarded && (
        <WelcomeDialog content={site} onDone={(u) => setUser(u)} />
      )}
      {user && user.onboarded && user.username_change_required && (
        <ForceRenameDialog currentUsername={user.username} onDone={(u) => setUser(u)} />
      )}
    </div>
  );
}
